import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/** Prepend 'Dr.' or 'Dra.' based on the doctor's stored title, or fall back
 * to 'Dr.' if the caller passes just a name string. Accepts either:
 *   - a full doctor object `{full_name, title}` (preferred)
 *   - a raw name string (legacy — assumes Dr.).
 * Never returns "Dr. Dr. …" if the stored name already starts with a title. */
export function drName(input) {
  if (!input) return "";
  if (typeof input === "string") {
    return /^\s*(dr\.?|dra\.?)\s+/i.test(input) ? input : `Dr. ${input}`;
  }
  const name = input.full_name || "";
  if (!name) return "";
  const title = input.title === "Dra." ? "Dra." : "Dr.";
  return /^\s*(dr\.?|dra\.?)\s+/i.test(name) ? name : `${title} ${name}`;
}

// =====================================================
// Date formatting (Ecuador locale, JJ/MM/AAAA, no ISO in UI)
// =====================================================
const _pad = (n) => String(n).padStart(2, "0");

/** Accepts Date | ISO string | "YYYY-MM-DD" and returns "DD/MM/YYYY". */
export function formatDateShort(input) {
  if (!input) return "";
  if (typeof input === "string" && /^\d{4}-\d{2}-\d{2}$/.test(input)) {
    const [y, m, day] = input.split("-");
    return `${day}/${m}/${y}`;
  }
  const d = input instanceof Date ? input : new Date(input);
  if (isNaN(d.getTime())) return "";
  return `${_pad(d.getDate())}/${_pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

/** Long format like "jueves 1 de octubre de 2026" (locale-aware). */
export function formatDateLong(input, lang = "es") {
  if (!input) return "";
  if (typeof input === "string" && /^\d{4}-\d{2}-\d{2}$/.test(input)) {
    const [y, m, day] = input.split("-").map(Number);
    return formatDateLong(new Date(y, m - 1, day), lang);
  }
  const d = input instanceof Date ? input : new Date(input);
  if (isNaN(d.getTime())) return "";
  const locale = { es: "es-EC", fr: "fr-FR", en: "en-US" }[lang] || "es-EC";
  return d.toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

/** Returns "HH:mm" (24h) from "HH:mm", "HH:mm:ss", ISO string, or Date. */
export function formatTime(input) {
  if (!input) return "";
  if (typeof input === "string" && /^\d{2}:\d{2}/.test(input)) return input.slice(0, 5);
  const d = input instanceof Date ? input : new Date(input);
  if (isNaN(d.getTime())) return "";
  return `${_pad(d.getHours())}:${_pad(d.getMinutes())}`;
}

/** Given date "YYYY-MM-DD" and time "HH:mm", returns "DD/MM/YYYY HH:mm". */
export function formatDateTimeParts(date, time) {
  const d = formatDateShort(date);
  const t = formatTime(time);
  if (d && t) return `${d} ${t}`;
  return d || t || "";
}

/** Accepts Date | ISO string and returns "DD/MM/YYYY HH:mm" (Ecuador local, 24h). */
export function formatDateTime(input) {
  if (!input) return "";
  const d = input instanceof Date ? input : new Date(input);
  if (isNaN(d.getTime())) return "";
  return `${_pad(d.getDate())}/${_pad(d.getMonth() + 1)}/${d.getFullYear()} `
       + `${_pad(d.getHours())}:${_pad(d.getMinutes())}`;
}

/**
 * i18n status label for appointments. Never returns raw English keys.
 * Falls back to Spanish then to the raw value if the dictionary misses.
 */
const _STATUS_DICT = {
  es: { confirmed: "Confirmada", completed: "Completada", cancelled: "Cancelada",
        pending: "Pendiente", pending_verification: "Pendiente",
        no_show: "No asistió", rescheduled: "Reprogramada" },
  fr: { confirmed: "Confirmé", completed: "Terminé", cancelled: "Annulé",
        pending: "En attente", pending_verification: "En attente",
        no_show: "Absent", rescheduled: "Reprogrammé" },
  en: { confirmed: "Confirmed", completed: "Completed", cancelled: "Cancelled",
        pending: "Pending", pending_verification: "Pending",
        no_show: "No-show", rescheduled: "Rescheduled" },
};
export function statusLabel(status, lang = "es") {
  const key = String(status || "").toLowerCase();
  const dict = _STATUS_DICT[lang] || _STATUS_DICT.es;
  return dict[key] || _STATUS_DICT.es[key] || status || "";
}

/** Returns a Date = today + N days (in local time). */
export function daysFromNow(n) {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d;
}
